import type { ArticleFragment } from '../graphql/fragments/Article.fragment';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';

import { computed } from 'vue';

import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router/composables';

import { useQuery } from '@/helpers/composables/useApollo';
import { captureMessageForSentry } from '@/helpers/sentry-helper';

import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

import { GetListArticleDocument, GetListArticleQuery } from '@/features/editorial/graphql/queries/GetListArticle.query';
import { BackdropProfile, ImageFormat } from '@/@types/graphql-types';

export function useListArticle() {
	const route = useRoute();

	const { language, country } = storeToRefs(useLanguageStore());
	const { activeDomain } = storeToRefs(useRoutingStore());

	const queryVariables = computed(() => {
		return {
			withOffers: true,
			fullPath: route.path,
			site: activeDomain.value,
			country: country.value,
			language: language.value,
			first: 10,
			profile: BackdropProfile.S1440,
			format: ImageFormat.Webp,
			filter: {
				ageCertifications: [],
				excludeGenres: [],
				excludeProductionCountries: [],
				objectTypes: [],
				productionCountries: [],
				subgenres: [],
				genres: [],
				packages: [],
				excludeIrrelevantTitles: false,
				presentationTypes: [],
				monetizationTypes: [],
				includeTitlesWithoutUrl: true,
			},
			authorPreview: true,
		};
	});

	const enabled = computed(() => route.name === 'app.editorial.subdomain.article');

	const {
		result,
		fetchMore,
		loading: isLoadingArticles,
		onError,
	} = useQuery<GetListArticleQuery>(GetListArticleDocument, queryVariables, { errorPolicy: 'all', enabled });

	onError(error => {
		captureMessageForSentry(
			'[GraphQL GetListArticle]:',
			{ error: error, where: 'Component: useListArticle.ts' },
			'error'
		);
	});

	const article = computed(() => result.value?.urlV2.node as ArticleFragment);

	type MainObject = AssertByTypename<ArticleFragment['mainObjects'][number]['node'], 'GenericTitleList'>;
	const mainObject = computed(() => (article.value.mainObjects[0]?.node ?? null) as MainObject | null);

	const isAtEndOfList = computed(() => !(mainObject.value?.titles.pageInfo.hasNextPage ?? false));

	// Load more function for pagination
	async function onLoadNext({ event }: { event: { target?: { complete: (len: number) => void } } }) {
		if (!event.target || mainObject.value?.__typename !== 'GenericTitleList') return;

		if (isLoadingArticles.value) return;

		const endCursor = mainObject.value.titles?.pageInfo?.endCursor;

		if (!endCursor) return;

		try {
			await fetchMore({
				variables: { after: endCursor },
				updateQuery(
					previousResult: GetListArticleQuery,
					{ fetchMoreResult }: { fetchMoreResult?: GetListArticleQuery }
				) {
					if (!fetchMoreResult) return previousResult;

					const previousArticle = previousResult.urlV2.node as ArticleFragment;
					const previousMainObject = previousArticle.mainObjects[0].node as MainObject;

					const fetchedTitles = (
						(fetchMoreResult.urlV2.node as ArticleFragment).mainObjects[0].node as MainObject
					).titles;

					if (previousMainObject.titles.edges == null || fetchedTitles.edges == null) return previousResult;

					previousMainObject.titles.edges.push(...fetchedTitles.edges);
					previousMainObject.titles.pageInfo = fetchedTitles.pageInfo;

					return previousResult;
				},
			});
		} catch (error: any) {
			captureMessageForSentry(
				'[GraphQL List Article Page Load More Titles Issue]:',
				{ error, where: 'Origin: useListArticle.ts, method: onLoadNext' },
				'error'
			);
		} finally {
			const edgeCount = (mainObject.value.titles.edges ?? []).length;
			event.target?.complete(edgeCount);
		}
	}

	return {
		article,
		isAtEndOfList,
		isLoadingArticles,
		onLoadNext,
	};
}
